import { Component, h, Method, Prop, State, Watch } from '@stencil/core';
import { default as languageStrings } from '../../global/l10n/ProductFamily.json';
import Util from '../../global/util';
import ProductFamilyDao from './product-family.dao';


@Component({
    tag: 'ti-product-family',
    styleUrl: 'product-family.scss',
    shadow: true
})
export class ProductFamily {

    private _commonQueryString: string = 'keyMatch={searchTerm}&tisearch={tisearch}&usecase=familyselection';
    private _productFamilyHierarchy: string[];
    private _selectedSubFamily: string;

    /**
     * Load Statuses
     */
    private _statusType = {
        LOADING: 'loading',
        READY: 'ready',
        ERROR: 'error'
    }

    /**
     * Holds the url of the selection tool for the given product family
     *
     * @type {string}
     */
    @State() familyUrl: string;

    /**
     * Keep track of the status of the component so that we can render a response to the user accordingly.
     *
     * @type {string}
     * @default StatusType.Loading
     */
    @State() status: string = this._statusType.LOADING;

    /**
     * Value of locale
     *
     * @type {string}
     * @default 'en-US'
     */
    @Prop() locale: string = 'en-US';
    @Watch('locale')
    handleLocaleChange(newLocale: string) {
        this.locale = Util.getValidLocale(newLocale);
    }

    /**
     * Value of currently selected product filter
     * Should be a comma-separated string of the family names in the selected family's hierarchy
     *
     * @type {string}
     */
    @Prop() productFilter: string;

    /**
     * URL for the search api
     *
     * @type {string}
     * @default '/search/productFamily'
     */
    @Prop() searchApiUrl: string = '/search/productFamily';

    /**
     * search term value. Used for metrics tracking.
     *
     * @type {string}
     */
    @Prop() searchString: string = '';

    /**
     * root URL for ti.com
     *
     * @type {string}
     * @default 'https://www.ti.com'
     */
    @Prop() tiHost: string = 'https://www.ti.com';

    /**
     * Value for tisearch metrics parameter
     *
     * @type {string}
     * @default 'universal_search'
     */
    @Prop() tisearch: string = 'universal_search';

    componentWillLoad() {
        this.locale = Util.getValidLocale(this.locale);
        if (this.productFilter) {
            this._updateData();
        }
    }

    /**
     * Method to set the url data
     */
    @Method()
    public async updateData(familyUrl: string) {
        if (familyUrl) {
            this._productFamilyHierarchy = this.productFilter.split(',');
            this.familyUrl = familyUrl;
            this.status = this._statusType.READY
        }
        else {
            this.status = this._statusType.ERROR;
        }
    }

    private async _updateData() {
        this._productFamilyHierarchy = this.productFilter.split(',');
        this._selectedSubFamily = this._productFamilyHierarchy[this._productFamilyHierarchy.length - 1];
        this.updateData(await ProductFamilyDao.searchProductFamily(this._selectedSubFamily, this.locale, this.searchApiUrl, this.tiHost));
    }

    render() {
        if (this.status !== this._statusType.READY) {
            return '';
        }
        const viewFamilyLabel = languageStrings[this.locale]["viewAllProducts"].replace('{0}', this._productFamilyHierarchy.join(' / '));
        // append filters query params to metrics string
        const url = document.location.href;
        let metricsUriText = this._commonQueryString;
        if (url.indexOf('#') >= 0) {
            url.split('#')[1].split('&').forEach((param) => {
                if (param.startsWith('f-') || param.startsWith('cf-')) {
                    metricsUriText = `${metricsUriText}&${param}`;
                }
            });
        }
        const viewFamilyUrl = Util.formatUrl(
            this.familyUrl,
            {
                searchTerm: this.searchString,
                tisearch: this.tisearch.toLocaleLowerCase()
            },
            metricsUriText
        );
        return (
            <ti-card appearance="plain-white">
                <a href={viewFamilyUrl}>
                    <span class="ti-product-family__icon">
                        <ti-svg-icon appearance="secondary" size="s" icon-set="objects">parametric-filter</ti-svg-icon>
                    </span>
                    <span innerHTML={viewFamilyLabel}></span>
                </a>
            </ti-card>
        )
    }
}
